import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Feature } from '@wwt/entities/search-response'
import { getSelectedOptionInfoFromFeature } from '@wwt/shared/utils/getSelectedOptionInfoFromFeature'

import { getFeatureLabel } from '../utils/getFeatureLabel.tsx'
import { getFeatureTooltipInfo } from '../utils/getFeatureTooltipInfo.ts'

export const useFeature = (
	feature: Feature,
	showSelectedOption = true,
	showFeatureName = true,
	forceIsClickable?: boolean | undefined
) => {
	const { t } = useTranslation('tooltips')

	const selectedOption = useMemo(
		() => getSelectedOptionInfoFromFeature(feature),
		[feature]
	)

	const { label, tooltipInfo } = useMemo(
		() => ({
			label: getFeatureLabel(
				selectedOption,
				feature,
				showSelectedOption,
				showFeatureName
			),
			tooltipInfo: getFeatureTooltipInfo(
				feature,
				selectedOption,
				t,
				showSelectedOption,
				showFeatureName
			)
		}),
		[selectedOption, feature, t, showSelectedOption, showFeatureName]
	)

	const title = useMemo(
		() => `${feature.name}: ${selectedOption.name}`,
		[feature, selectedOption]
	)

	const iconValues = useMemo(
		() => ({
			groupIconName: feature.group?.iconName,
			featureIconName: feature.iconName,
			optionIconName: selectedOption.iconName
		}),
		[feature, selectedOption]
	)

	const isClickable = useMemo(
		() =>
			forceIsClickable === undefined
				? 'options' in feature && feature.options.length > 1
				: forceIsClickable,
		[forceIsClickable, feature]
	)

	return useMemo(
		() => ({
			label,
			tooltipInfo,
			selectedOption,
			title,
			iconValues,
			isClickable,
			isExist: selectedOption.isExist
		}),
		[label, tooltipInfo, selectedOption, title, iconValues, isClickable]
	)
}
